import { FC, ReactNode, createContext, useContext, useRef } from "react";
import { useStoreWithEqualityFn } from "zustand/traditional";
import { createCompanyFilterStore } from "../store/createCompanyFilterStore";
import type { CompanyFilterStore } from "../store/types";
import type { GeographyOptions } from "../modules/geography/options";
import type { ItSpendOptions } from "../modules/itSpend/options";
import type { TargetOrganizationsOptions } from "../modules/targetOrganizations/options";
import type { UploadedAccountsOptions } from "../modules/uploadedAccounts/options";
import type { KnownCompaniesOptions } from "../modules/knownCompanies/options";
import type { IntentOptions } from "../modules/intent/options";
import type { FilterLookups } from "../types";

type CompanyFiltersStoreApi = ReturnType<typeof createCompanyFilterStore>;

type OptionsMap = {
  targetOrganizations?: TargetOrganizationsOptions;
  uploadedAccounts?: UploadedAccountsOptions;
  knownCompanies?: KnownCompaniesOptions;
  geography?: GeographyOptions;
  globalPresence?: undefined;
  rankings?: undefined;
  promotionDate?: undefined;
  employees?: undefined;
  revenue?: undefined;
  companyType?: undefined;
  industry?: undefined;
  productCategories?: undefined;
  productAttributes?: undefined;
  products?: undefined;
  vendors?: undefined;
  itSpend?: ItSpendOptions;
  intent?: IntentOptions;
  fitScore?: undefined;
  intentScore?: undefined;
  contactMode?: undefined;
  contactJobSeniority?: undefined;
  contactJobFunction?: undefined;
  contactJobTitle?: undefined;
  contactLocation?: undefined;
  contactTimeInRole?: undefined;
  buyerIntentProducts?: undefined;
  buyerIntentVendors?: undefined;
  buyerIntentCategories?: undefined;
  buyerIntentAttributes?: undefined;
  modelScore?: undefined;
  companyConfig?: undefined;
  financingEvents?: undefined;
};

const StoreContext = createContext<CompanyFiltersStoreApi | null>(null);
const OptionsContext = createContext<OptionsMap | undefined>(undefined);
const LookupsContext = createContext<FilterLookups | undefined>(undefined);

export type CompanyFilterProviderProps = {
  children: ReactNode;
  options?: OptionsMap;
  lookups?: FilterLookups;
};

export const CompanyFilterProvider: FC<CompanyFilterProviderProps> = ({
  children,
  options,
  lookups,
}) => {
  const storeRef = useRef<CompanyFiltersStoreApi | null>(null);
  if (!storeRef.current) {
    storeRef.current = createCompanyFilterStore();
  }

  return (
    <StoreContext.Provider value={storeRef.current}>
      <OptionsContext.Provider value={options}>
        <LookupsContext.Provider value={lookups}>
          {children}
        </LookupsContext.Provider>
      </OptionsContext.Provider>
    </StoreContext.Provider>
  );
};

export function useCompanyFiltersStore<T>(
  selector: (state: CompanyFilterStore) => T,
  equalityFn?: (a: T, b: T) => boolean
): T {
  const store = useContext(StoreContext);
  if (!store) {
    throw new Error(
      "useCompanyFiltersStore must be used within CompanyFilterProvider"
    );
  }
  return useStoreWithEqualityFn(store, selector, equalityFn);
}

export function useCompanyFiltersStoreApi(): CompanyFiltersStoreApi {
  const store = useContext(StoreContext);
  if (!store) {
    throw new Error(
      "useCompanyFiltersStoreApi must be used within CompanyFilterProvider"
    );
  }
  return store;
}

export function useCompanyFilterOptionsMap(): OptionsMap | undefined {
  return useContext(OptionsContext);
}

export function useCompanyFilterLookups(): FilterLookups | undefined {
  return useContext(LookupsContext);
}
